import { types, } from 'mobx-state-tree';
import { enqueueSnackbar as notistackEnqueueSnackbar } from 'notistack';
import ConfirmDialog from './ConfirmDialogModel';
import ErrorDialog from './ErrorDialogModel';
import MessageDialog from './MessageDialogModel';
const NavItem = types.model({
    id: types.identifier,
    name: types.string,
    icon: types.maybe(types.string),
    path: types.maybe(types.string),
    dividerAfter: types.optional(types.boolean, false),
    dividerBefore: types.optional(types.boolean, false),
    children: types.maybe(types.array(types.late(() => NavItem))),
    expanded: types.optional(types.boolean, false),
    allowedRoles: types.array(types.string),
    requiredFeatures: types.optional(types.array(types.string), []),
})
    .actions((self) => ({
    toggle() {
        self.expanded = !self.expanded;
    },
}));
function navItemVisibleForUser(item, userRoles, organization) {
    const roleAllowed = !item.allowedRoles?.length
        || item.allowedRoles.some((role) => userRoles.includes(role));
    const featuresOk = !item.requiredFeatures?.length
        || item.requiredFeatures.every((flag) => !!organization?.[flag]);
    return roleAllowed && featuresOk;
}
const enqueueSnackbar = (...params) => {
    if (notistackEnqueueSnackbar) {
        notistackEnqueueSnackbar(...params);
    }
};
export const NavStore = types.model('NavStore', {
    confirmDialog: ConfirmDialog,
    errorDialog: ErrorDialog,
    messageDialog: MessageDialog,
    blockInput: types.boolean,
    items: types.array(NavItem),
    currentItem: types.reference(NavItem),
})
    .actions((self) => ({
    showConfirmDialog(title, message, confirmHandler, confirmButtonLabel, cancelHandler = null, cancelButtonLabel = 'Cancel') {
        self.confirmDialog.showConfirm(title, message, confirmHandler, cancelHandler, confirmButtonLabel, cancelButtonLabel);
    },
    hideConfirmDialog() {
        self.confirmDialog.open = false;
    },
    showMessageDialog(title, message) {
        self.messageDialog.showConfirm(title, message);
    },
    hideMessageDialog() {
        self.messageDialog.open = false;
    },
    showErrorDialog(title, message) {
        self.errorDialog.showError(title, message);
    },
    hideErrorDialog() {
        self.errorDialog.open = false;
    },
    showBlockInput() {
        self.blockInput = true;
    },
    clearBlockInput() {
        self.blockInput = false;
    },
    showSnackBar(message, severity = undefined, key = undefined) {
        enqueueSnackbar(message, {
            preventDuplicate: true,
            key,
            variant: severity,
        });
    },
}));
const items = [
    {
        id: 'ai-visibility-geo',
        name: 'AI Visibility (GEO)',
        path: '/app/guides',
        icon: 'travelExplore',
        allowedRoles: ['FULLADMIN', 'SUPERUSER', 'AIADMIN'],
        requiredFeatures: ['enableGeo'],
    },
    {
        id: 'create',
        name: 'Create',
        icon: 'plusSquare',
        expanded: false,
        allowedRoles: ['FULLADMIN', 'SUPERUSER'],
        children: [
            {
                id: 'microcast',
                name: 'Microcast',
                path: '/app/microcast-editor/new',
                icon: 'microcast',
            },
            {
                id: 'create-course',
                name: 'Course',
                path: '/app/course-editor/new',
                icon: 'cog',
            },
            {
                id: 'create-course-with-wizard',
                name: 'AI Content Creator',
                path: '/app/course-editor/newWithWizard',
                icon: 'openai',
            },
            {
                id: 'create-program',
                name: 'Program',
                path: '/app/program-editor/new',
                icon: 'map',
            },
            {
                id: 'create-document',
                name: 'Document',
                path: '/app/document-editor/new',
                icon: 'map',
            },
        ],
    },
    {
        id: 'microcast',
        name: 'Microcasts',
        path: '/app/microcasts',
        icon: 'microcast',
        allowedRoles: ['FULLADMIN', 'SUPERUSER'],
    },
    {
        id: 'courses',
        name: 'Courses',
        path: '/app/courses',
        icon: 'course',
        allowedRoles: ['FULLADMIN', 'SUPERUSER'],
    },
    {
        id: 'programs',
        name: 'Programs',
        path: '/app/programs',
        icon: 'program',
        allowedRoles: ['FULLADMIN', 'SUPERUSER'],
    },
    {
        id: 'documents',
        name: 'Documents',
        path: '/app/documents',
        icon: 'document',
        allowedRoles: ['FULLADMIN', 'SUPERUSER'],
    },
    {
        id: 'meetings',
        name: 'Meetings',
        path: '/app/meetings',
        icon: 'archive',
        allowedRoles: ['FULLADMIN', 'SUPERUSER'],
        requiredFeatures: ['enableMeetings'],
    },
    {
        id: 'hubs',
        name: 'Hubs',
        path: '/app/hubs',
        icon: 'archive',
        allowedRoles: ['FULLADMIN', 'SUPERUSER'],
        requiredFeatures: ['enableHubs'],
    },
    {
        id: 'resources',
        name: 'Resources',
        icon: 'resources',
        expanded: false,
        allowedRoles: ['FULLADMIN', 'SUPERUSER'],
        children: [
            {
                id: 'courses-videos',
                name: 'Videos',
                path: '/app/assets/video',
                icon: 'playCircle',
            },
            {
                id: 'courses-images',
                name: 'Images',
                path: '/app/assets/image',
                icon: 'image',
            },
            {
                id: 'courses-pdfs',
                name: 'PDFs',
                path: '/app/assets/pdf',
                icon: 'pdf',
            },
            {
                id: 'courses-other',
                name: 'SCORM / Other Assets',
                path: '/app/assets/other',
                icon: 'image',
            },
            {
                id: 'courses-instructors',
                name: 'Instructors',
                path: '/app/instructors',
                icon: 'maskGroup',
            },
            {
                id: 'courses-certifications',
                name: 'Certifications',
                path: '/app/certifications',
                icon: 'fileStar',
            },
            {
                id: 'resources-products',
                name: 'Products',
                path: '/app/products',
                icon: 'storefront',
            },
            {
                id: 'resources-catalog-taxonomy',
                name: 'Catalog Taxonomy',
                path: '/app/catalog-taxonomy',
                icon: 'category',
            },
            {
                id: 'resources-categories',
                name: 'Categories',
                path: '/app/ecommerce-categories',
                icon: 'category',
            },
            {
                id: 'resources-web-pages',
                name: 'Web Pages',
                path: '/app/web-pages',
                icon: 'article',
            },
        ],
    },
    {
        id: 'portal',
        name: 'Portal',
        path: '/app/portals',
        icon: 'portal',
        expanded: false,
        allowedRoles: ['FULLADMIN', 'SUPERUSER'],
        children: [
            {
                id: 'portal-details',
                name: 'Details',
                title: 'Portal Details',
                path: '/app/details',
                icon: 'infoCircle',
            },
            {
                id: 'portal-categories',
                name: 'Categories',
                path: '/app/categories',
                icon: 'palette',
            },
            {
                id: 'portal-design',
                name: 'Design',
                title: 'Portal Designer',
                path: '/app/design',
                icon: 'designServices',
            },
            {
                id: 'portal-ai-config',
                name: 'AI Configuration',
                title: 'AI Configuration',
                path: '/app/aiconfig',
                icon: 'smartToy',
            },
            {
                id: 'portal-stylesheet',
                name: 'Stylesheet',
                title: 'Portal Stylesheet',
                path: '/app/stylesheet',
                icon: 'css',
            },
            {
                id: 'portal-memberships',
                name: 'Memberships',
                path: '/app/portal-memberships',
                icon: 'cardMembership',
            },
            {
                id: 'portal-members',
                name: 'Members',
                path: '/app/portal-members',
                icon: 'people',
            },
            {
                id: 'portal-kickers',
                name: 'Portal Kickers',
                path: '/app/portal-kickers',
                icon: 'brightnessUp',
            },
        ],
    },
    {
        id: 'tools',
        name: 'Tools',
        icon: 'cog',
        expanded: false,
        allowedRoles: ['FULLADMIN', 'SUPERUSER'],
        children: [
            {
                id: 'email-templates',
                name: 'Email Templates',
                path: '/app/email-templates',
                icon: 'brightnessUp',
            },
            {
                id: 'discussion',
                name: 'Discussion Groups',
                path: '/app/discussion',
                icon: 'user',
                requiredFeatures: ['enableDiscussionGroups'],
            },
            {
                id: 'engagement-flows',
                name: 'Engagement Flows',
                path: '/app/engagement-flows',
                icon: 'brightnessUp',
            },
        ],
    },
    {
        id: 'reportingTab',
        name: 'Reporting',
        icon: 'plusSquare',
        expanded: false,
        allowedRoles: ['FULLADMIN', 'SUPERUSER'],
        children: [
            {
                id: 'reporting',
                name: 'Engagement',
                path: '/app/reporting/engagement',
                icon: 'reporting',
            },
            {
                id: 'compliance',
                name: 'Compliance',
                path: '/app/reporting/compliance',
                icon: 'compliance',
            },
        ],
    },
    {
        id: 'aiReportingTab',
        name: 'AI Reporting',
        icon: 'plusSquare',
        expanded: false,
        allowedRoles: ['FULLADMIN', 'SUPERUSER', 'AIADMIN'],
        children: [
            {
                id: 'ai-dashboard',
                name: 'AI Dashboard',
                path: '/app/reporting/dashboard',
                icon: 'dashboard',
            },
            {
                id: 'conversation-summary',
                name: 'Conversation Summary',
                path: '/app/reporting/conversation-summary',
                icon: 'dashboard',
            },
            {
                id: 'conversations',
                name: 'Conversations',
                path: '/app/reporting/conversations',
                icon: 'dashboard',
            },
            {
                id: 'scored-conversations',
                name: 'Scored conversations',
                path: '/app/reporting/scored-conversations',
                icon: 'dashboard',
                allowedRoles: ['SUPERUSER'],
            },
            {
                id: 'top-products',
                name: 'Top Products',
                path: '/app/reporting/top-products',
                icon: 'dashboard',
            },
            {
                id: 'topics',
                name: 'Topics',
                path: '/app/reporting/topics',
                icon: 'dashboard',
            },
        ],
    },
    {
        id: 'geoReportingTab',
        name: 'GEO Reporting',
        icon: 'plusSquare',
        expanded: false,
        allowedRoles: ['SUPERUSER'],
        children: [
            {
                id: 'geo-reporting-overview',
                name: 'Overview',
                path: '/app/reporting/geo',
                icon: 'dashboard',
                allowedRoles: ['SUPERUSER'],
            },
            {
                id: 'geo-reporting-traffic',
                name: 'Traffic & Conversion',
                path: '/app/reporting/geo/traffic',
                icon: 'dashboard',
                allowedRoles: ['SUPERUSER'],
            },
        ],
    },
    {
        id: 'organization',
        name: 'Organization',
        icon: 'briefcase',
        expandedBefore: false,
        dividerBefore: true,
        allowedRoles: ['FULLADMIN', 'SUPERUSER'],
        children: [
            {
                id: 'details',
                name: 'Details',
                icon: 'briefcase',
                path: '/app/organization-summary',
            },
            {
                id: 'users',
                path: '/app/users',
                icon: 'users',
                name: 'Admin Users',
            },
            {
                id: 'organization-libraries',
                name: 'Libraries',
                path: '/app/libraries',
                icon: 'archive',
            },
            {
                id: 'repositories',
                name: 'Repositories',
                path: '/app/repositories',
                icon: 'archive',
                allowedRoles: ['SUPERUSER'],
            },
            {
                id: 'libraryShares',
                name: 'Marketplace',
                path: '/app/libraryShares',
                icon: 'archive',
                allowedRoles: ['FULLADMIN', 'SUPERUSER'],
            },
        ],
    },
    {
        id: 'data-hub',
        name: 'Data Hub',
        icon: 'archive',
        expandedBefore: false,
        dividerBefore: true,
        allowedRoles: ['FULLADMIN', 'AIADMIN', 'SUPERUSER'],
        children: [
            {
                id: 'web-crawl',
                path: '/app/web-crawl',
                icon: 'travelExplore',
                name: 'Web Crawl',
            },
            {
                id: 'shopify-shops',
                path: '/app/shopify-shops',
                icon: 'storefront',
                name: 'Shopify',
            },
            {
                id: 'google-drive-sync',
                path: '/app/google-drive-sync',
                icon: 'google',
                name: 'Google Drive Sync',
            },
        ],
    },
    {
        id: 'customers',
        name: 'Customers',
        path: '/app/customers',
        icon: 'user',
        allowedRoles: ['AIADMIN', 'FULLADMIN', 'SUPERUSER'],
    },
    {
        id: 'profiles',
        name: 'AI Profiles',
        path: '/app/profiles',
        icon: 'user',
        allowedRoles: ['AIADMIN', 'FULLADMIN', 'SUPERUSER'],
    },
    {
        id: 'system',
        name: 'System',
        icon: 'system',
        allowedRoles: ['SUPERUSER'],
        children: [
            {
                id: 'system-organizations',
                path: '/app/organizations',
                icon: 'briefcase',
                name: 'Organizations',
            },
            {
                id: 'system-client-ai-configs',
                path: '/app/client-ai-configs',
                icon: 'cog',
                name: 'Client AI Configs',
            },
            {
                id: 'system-system-prompts',
                path: '/app/system-prompts',
                icon: 'openai',
                name: 'System Prompts',
            },
        ],
    },
    {
        id: 'system-reporting',
        name: 'System Reporting',
        icon: 'reporting',
        expanded: false,
        allowedRoles: ['SUPERUSER'],
        children: [
            {
                id: 'sessions',
                name: 'Sessions',
                path: '/app/reporting/sessions',
                icon: 'dashboard',
            },
            {
                id: 'page-views',
                name: 'Page Views',
                path: '/app/reporting/page-views',
                icon: 'dashboard',
            },
            {
                id: 'ai-conversations',
                name: 'AI Conversations',
                path: '/app/reporting/ai-conversations',
                icon: 'chat',
            },
        ],
    },
    {
        id: 'ai-data-hub',
        name: 'Data Hub',
        icon: 'archive',
        allowedRoles: ['AIADMIN'],
        children: [
            {
                id: 'ai-microcasts',
                name: 'Microcasts',
                path: '/app/microcasts',
                icon: 'microcast',
            },
            {
                id: 'ai-documents',
                name: 'Documents',
                path: '/app/documents',
                icon: 'document',
            },
            {
                id: 'ai-web-crawl',
                name: 'Web Crawl',
                path: '/app/web-crawl',
                icon: 'travelExplore',
            },
        ],
    },
    {
        id: 'ai-resources',
        name: 'Resources',
        icon: 'resources',
        expanded: false,
        allowedRoles: ['AIADMIN'],
        children: [
            {
                id: 'ai-resources-products',
                name: 'Product',
                path: '/app/products',
                icon: 'storefront',
            },
            {
                id: 'ai-resources-categories',
                name: 'Categories',
                path: '/app/ecommerce-categories',
                icon: 'category',
            },
            {
                id: 'ai-resources-web-pages',
                name: 'Web Pages',
                path: '/app/web-pages',
                icon: 'article',
            },
            {
                id: 'ai-resources-videos',
                name: 'Videos',
                path: '/app/assets/video',
                icon: 'playCircle',
            },
            {
                id: 'ai-resources-images',
                name: 'Images',
                path: '/app/assets/image',
                icon: 'image',
            },
            {
                id: 'ai-resources-pdfs',
                name: 'PDFs',
                path: '/app/assets/pdf',
                icon: 'pdf',
            },
            {
                id: 'ai-resources-other',
                name: 'SCORM / Other Assets',
                path: '/app/assets/other',
                icon: 'image',
            },
        ],
    },
    {
        id: 'ai-design-configure',
        name: 'Design & Configure',
        icon: 'designServices',
        allowedRoles: ['AIADMIN'],
        children: [
            {
                id: 'ai-configurations',
                name: 'AI Configurations',
                path: '/app/aiconfig',
                icon: 'smartToy',
            },
            {
                id: 'ai-design',
                name: 'Design',
                path: '/app/design',
                icon: 'designServices',
            },
            {
                id: 'ai-stylesheets',
                name: 'Style Sheets',
                path: '/app/stylesheet',
                icon: 'css',
            },
        ],
    },
    {
        id: 'ai-portal',
        name: 'Portal',
        icon: 'portal',
        allowedRoles: ['AIADMIN'],
        children: [
            {
                id: 'portal-details-ai',
                name: 'Portal Details',
                path: '/app/details',
                icon: 'infoCircle',
            },
            {
                id: 'portal-categories-ai',
                name: 'Portal Categories',
                path: '/app/categories',
                icon: 'palette',
            },
        ],
    },
    {
        id: 'ai-reporting',
        name: 'Reporting',
        icon: 'reporting',
        allowedRoles: ['AIADMIN'],
        children: [
            {
                id: 'ai-reporting-route',
                name: 'AI Reporting',
                path: '/app/reporting/ai-conversations',
                icon: 'dashboard',
            },
        ],
    },
    {
        id: 'ai-organization',
        name: 'Organization',
        icon: 'briefcase',
        allowedRoles: ['AIADMIN'],
        children: [
            {
                id: 'organization-users',
                name: 'Users',
                path: '/app/users',
                icon: 'users',
            },
            {
                id: 'organization-client-ai-config',
                name: 'Client AI Config',
                path: '/app/client-ai-configs',
                icon: 'cog',
            },
        ],
    },
];
function findNavLabelDepthFirst(nodes, targetPath) {
    for (const node of nodes) {
        if (node.path) {
            const p = String(node.path).replace(/\/$/, '');
            if (p === targetPath) {
                return node.name;
            }
        }
        if (node.children?.length) {
            const found = findNavLabelDepthFirst(node.children, targetPath);
            if (found !== undefined) {
                return found;
            }
        }
    }
    return undefined;
}
/**
 * Sidebar label for an app list path (e.g. `/app/courses` → `Courses`), matching the first
 * matching entry in {@link items} in depth-first order — same labels as AdminSideBar.
 * Returns `undefined` if the path is not in the nav config.
 */
export function getNavLabelForAppPath(listPath) {
    if (!listPath || typeof listPath !== 'string') {
        return undefined;
    }
    const normalized = listPath.replace(/\/$/, '') || listPath;
    return findNavLabelDepthFirst(items, normalized);
}
/**
 * Sidebar routes the current user can see (same rules as AdminSideBar + NavStore items).
 * Sent to AdminAgent so **navigate_admin_screen** uses real paths (e.g. AI Dashboard → /app/reporting/dashboard).
 */
export function collectNavRoutesForAdminAgent(userRoles, organization, options) {
    const maxRoutes = options?.maxRoutes ?? 150;
    const byPath = new Map();
    const merge = (path, label, section) => {
        const prev = byPath.get(path);
        if (!prev) {
            byPath.set(path, { path, label, ...(section ? { section } : {}) });
            return;
        }
        if (prev.label !== label) {
            prev.label = `${prev.label} / ${label}`;
        }
        if (section && prev.section !== section) {
            prev.section = prev.section ? `${prev.section}; ${section}` : section;
        }
    };
    const walk = (navItems, parentSectionLabels) => {
        for (const item of navItems) {
            if (!navItemVisibleForUser(item, userRoles, organization))
                continue;
            const sectionTrail = parentSectionLabels.length > 0 ? parentSectionLabels.join(' > ') : undefined;
            if (item.path && String(item.path).trim()) {
                merge(String(item.path).trim(), item.name, sectionTrail);
            }
            const childParentLabels = [...parentSectionLabels, item.name];
            if (item.children && item.children.length > 0) {
                walk(item.children, childParentLabels);
            }
        }
    };
    walk(items, []);
    const out = Array.from(byPath.values()).sort((a, b) => a.path.localeCompare(b.path));
    return out.slice(0, maxRoutes);
}
export const navStore = NavStore.create({
    errorDialog: {
        open: false,
        title: '',
        message: '',
    },
    confirmDialog: {
        open: false,
        title: 'Confirm Action',
        message: 'This is the message you will see',
        confirmButtonLabel: 'OK',
        cancelButtonLabel: 'Cancel',
    },
    messageDialog: {
        open: false,
        title: '',
        message: '',
    },
    blockInput: false,
    items,
    currentItem: 'dashboard',
});
//# sourceMappingURL=NavStore.js.map